import React, { useEffect, useRef } from "react";
import _isEqual from "lodash-es/isEqual";

import { PageError, PageLoading } from "../components/shared/message/Message";
import stateTypes from "./constants/stateTypes";
import getAxios from "./axios";
import queryString from "query-string";
import EsV2QueryParams from "./constants/EsV2QueryParams";
import { trackPopUp } from "../redux/dispatchers/userDispatcher";
import moment from "moment";

function loadingHelper(statuses, render, conditions) {
    // check pre conditions
    if (conditions && conditions.pre) {
        for (const condition of conditions.pre) {
            const bool =
                typeof condition.check === "function" ?
                condition.check() :
                condition.check;

            if (!bool)
                return typeof condition.render === "function" ? (
                    condition.render(condition.error)
                ) : ( <
                    PageError error = { condition.error }
                    />
                );
        }
    }

    // check statuses
    let count = 0;
    for (const status of statuses) {
        if (status.state === stateTypes.STATE_FAIL) {
            return typeof status.render === "function" ? (
                status.render(status.error)
            ) : ( <
                PageError error = { status.error }
                />
            );
        } else if (status.state === stateTypes.STATE_DONE) {
            count++;
        } else {
            if (status.check === false) {
                count++;
            }
        }
    }

    if (statuses.length !== count) {
        return <PageLoading / > ;
    }

    // check post conditions
    if (conditions && conditions.post) {
        for (const condition of conditions.post) {
            const bool =
                typeof condition.check === "function" ?
                condition.check() :
                condition.check;

            if (!bool)
                return typeof condition.render === "function" ? (
                    condition.render(condition.error)
                ) : ( <
                    PageError error = { condition.error }
                    />
                );
        }
    }

    return render();
}

/**
 * Removes 00's from decimal if present, and prepends rupee symbol
 */
function getFormattedINRValues(inr, defaultVal = null) {
    return inr ?
        `₹${
        inr.toFixed(2).split(".")[1] === "00" ? inr.toFixed(0) : inr.toFixed(2)
      }` :
        defaultVal;
}

function getThousandsFormatCurrency(inr = 0) {
    if (parseFloat(inr)) {
        const value = new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
        }).format(inr);

        return value.includes(".00") ? value.split(".00")[0] : value;
    } else {
        return "₹" + inr;
    }
}

function debounce(fn, delay = 1000) {
    let timerId;
    return function(val, clearDebounce = false) {
        let args = [...arguments];
        if (timerId) {
            clearTimeout(timerId);
        }
        timerId = setTimeout(() => {
            fn.apply(this, args);
            timerId = null;
        }, delay);

        if (clearDebounce) {
            clearTimeout(timerId);
        }
    };
}

function getCache(ref, args, fn) {
    let value;

    console.log("get cache fn called");

    if (!ref.current) {
        value = fn(...args);
        ref.current = {
            args: [...args],
            value: value,
        };
    } else {
        if (
            ref.current.args.length === 0 ||
            args.length === 0 ||
            !_isEqual(ref.current.args, args)
        ) {
            value = fn(...args);
            ref.current.args = [...args];
            ref.current.value = value;
        } else {
            value = ref.current.value;
        }
    }

    return value;
}

// return top scroll value
function getScroll() {
    let pageYOffset;
    if (pageYOffset !== undefined) {
        return [pageYOffset];
    } else {
        let sy,
            d = document,
            r = d.documentElement,
            b = d.body;
        sy = r.scrollTop || b.scrollTop || 0;
        return sy;
    }
}

function usePrevious(value) {
    // The ref object is a generic container whose current property is mutable ...
    // ... and can hold any value, similar to an instance property on a class
    const ref = useRef();

    // Store current value in ref
    useEffect(() => {
        ref.current = value;
    }, [value]); // Only re-run if value changes

    // Return previous value (happens before update in useEffect above)
    return ref.current;
}

function useOnUpdate(fn, inputs) {
    const didMountRef = useRef(false);

    useEffect(() => {
        if (didMountRef.current)
            return fn();
        else
            didMountRef.current = true;
    }, inputs);
}

function makeAndSaveBlob(data, fileName, fileExtension) {
    /**
     * Make blob
     */

    let contentType = "";
    switch (fileExtension) {
        case "xlsx":
            contentType =
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            break;
        case "pdf":
            contentType = "application/pdf";
            break;
        default:
            break;
    }

    const blob = new Blob([data], {
        type: contentType,
    });

    /**
     * Save blob
     */

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // for IE
        window.navigator.msSaveOrOpenBlob(blob);
    } else {
        // for other browsers
        const url = window.URL.createObjectURL(blob);
        saveLink(url, fileName, fileExtension);
    }
}

function saveLink(url, fileName, fileExtension) {
    // for other browsers
    const link = document.createElement("a");

    // set attributes on link
    link.style.display = "none";
    link.href = url;
    link.download = `${fileName}.${fileExtension}`;

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    if (typeof link.download === "undefined") {
        link.target = "_blank";
    }

    // add to DOM and click
    document.body.appendChild(link);
    link.click();

    // clean up
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * This function removes extra precision from a number
 * @param {number} num number to remove precision from
 * @returns {number} number with removed precision
 */
function removeExtraPrecision(num) {
    // check if num is a whole number
    if (num % 1 === 0) {
        return num;
    } else {
        return parseFloat(num.toFixed(2));
    }
}

function parseIntOr0(val) {
    const result = parseInt(val);
    return isNaN(result) ? 0 : result;
}

function handleActionType(
    actionType,
    actionData,
    history,
    otherData = {},
    trackingParams,
    trackParamsCall,
) {
    // otherData keys
    // getCategoryAndSubCategoryNames - CATEGORY, SUBCATEGORY
    // id - PRODUCT
    // commercialName - PRODUCT
    switch (actionType) {
        case "SWITCH_OUTLET":
            history.push("/buyer/outlet")
            break;
        case "WEB_VIEW":
            handleWebViewActonType(actionData, history, trackingParams, trackParamsCall);
            break;
        case "SEARCH_QUERY":
            handleSearchQueryActionType(actionData, history, otherData);
            break;
        case "CATEGORY":
            handleSwitchCategoryActionType(actionData, history, otherData, trackingParams, trackParamsCall);
            break;
        case "SUBCATEGORY":
            handleSwitchCategoryActionType(actionData, history, otherData, trackingParams, trackParamsCall);
            break;
        case "SWITCH_CATEGORY":
            handleSwitchCategoryActionType(actionData, history, otherData);
            break;
        case "OFFERS":
            handleSwitchCategoryActionType(actionData, history, otherData, trackingParams, trackParamsCall);
            break;
        case "REFERRAL":
            handleReferralActionType(history, otherData);
            break;
        case "CART":
            handleCartActionType(history, otherData);
            break;
        case "HYPERPURE_CREDITS":
            handleHyperpureCreditsActionType(history, otherData, trackingParams, trackParamsCall);
            break;
        case "CREDIT_NOTE":
            handleCreditNoteActionType(actionData, history, otherData);
            break;
        case "CREDIT_LIMIT":
            handleCreditLimitActionType(history, otherData, trackingParams, trackParamsCall);
            break;
        case "ORDER_HISTORY":
            handleOrderHistoryActionType(history, otherData);
            break;
        case "ORDER":
            handleOrderActionType(actionData, history, otherData);
            break;
        case "PRODUCT":
            handleProductActionType(actionData, history, otherData);
            break;
        case "PAY_LATER":
            handlePayLaterActionType(history, otherData);
            break;
        case "REWARDS":
            handleRewardsActionType(history);
            break;
        case "PROFILE":
            handleProfileActionType(history);
            break;
        case "HELP":
            handleHelpActionType(history);
            break;
        case "LOGOUT":
            otherData.logout();
            break;
        case "DOWNLOAD_STATEMENT":
            otherData.downloadStatement();
            break;
        case "EMAIL_STATEMENT":
            otherData.emailStatement();
            break;
        case "REQUEST_NEW_PRODUCT":
            history.push("/request-product?source=side_menu&searchText=")
            break;
        case "PRODUCT_DETAILS":
            history.push(`/buyer/product-detail/${actionData.ProductId}?source=${actionData.Source}&referenceId=${actionData.ReferenceId}&referenceType=${actionData.ReferenceType}`)
            break;
        case "CREDIT_PAY" : 
            handleCreditPayEvent(otherData);
            break;
        case "HOME":
            history.push("/");
            break;
        case "CONSENT" : 
            handleConsentPopup ( trackingParams , trackParamsCall)
            break;
        case "DISMISS_NUDGE":
            handleDismissNudgeCTA(trackingParams, trackParamsCall);
            break;
        default:
            break;
    }
}

function handleWebViewActonType(actionData, history, trackingParams, trackParamsCall) {
    if (trackingParams && typeof trackParamsCall == "function") {
        trackParamsCall(trackingParams)
    }
    if (actionData.Endpoint) {
        getAxios(true)
            .get(actionData.Endpoint)
            .then(({ data }) => {
                const url = new URL(data.response.WebViewURL);
                history.push(url.pathname); // TODO, currently not consider query params
            })
            .catch((err) => {
                console.error(err);
            });
    } else if (actionData.WebViewURL) {
        const redirectTo = actionData.WebViewURL;

        if (!redirectTo.includes(".com")) {
            history.push(redirectTo);
        } else {
            let urlWoHost = redirectTo.split(".com");
            let urlWoHostPrefix = urlWoHost[1][0] === "/" ? "" : "/";
            history.push(urlWoHostPrefix + urlWoHost[1]);
        }
    } else {
        history.push("");
    }
}

function handleCreditPayEvent (otherData) {
  if (otherData.toggleCreditModal) {
    otherData.toggleCreditModal();
  }
  return 
}

function handleConsentPopup (trackingParams , trackParamsCall) {
    console.log(trackingParams,trackParamsCall,"PPP")
    if (typeof trackParamsCall === "function") {
        trackParamsCall(trackingParams);
    }
}

function handleSearchQueryActionType(actionData, history, otherData = {}) {
    const title = encodeURIComponent(actionData.Title);
    let redirectUrl = `/buyer/query/${title}/search?${actionData.SearchQuery}`;
    if (otherData.sendReference) {
        redirectUrl = redirectUrl + `&referenceId=${actionData.ReferenceId}&referenceType=${actionData.ReferenceType}`
    }
    if (otherData.reload) {
        window.location.href = redirectUrl
    } else {
        history.push(
            redirectUrl
        );
    }
}

function handleSwitchCategoryActionType(actionData, history, otherData = {}, trackingParams, trackingParamsCall) {
    if (trackingParams && typeof trackingParamsCall == "function") {
        trackingParamsCall(trackingParams)
    }
    const {
        CategoryId,
        SubCategoryId,
        ReferenceId,
        ReferenceType
    } = actionData
    let redirectUrl = `/buyer/catalog/${CategoryId}/${SubCategoryId}`;
    if (otherData.sendReference) {
        redirectUrl = redirectUrl + `?referenceId=${ReferenceId}&referenceType=${ReferenceType}`
    }
    if (otherData.reload) {
        window.location.href = redirectUrl
    } else {
        history.push(
            redirectUrl
        );
    }
}


function handleReferralActionType(history, otherData) {
    if (otherData.reload) {
        window.location.href = "/referral"
    } else {
        history.push(`/referral`);
    }
}

function handleCartActionType(history, otherData) {
    if (otherData.reload) {
        window.location.href = "/buyer/cart"
    } else {
        history.push(`/buyer/cart`);
    }
}

function handleHyperpureCreditsActionType(history, otherData, trackingParams, trackingParamsCall) {
    if (trackingParams && typeof trackingParamsCall == "function") {
        trackingParamsCall(trackingParams)
    }
    if (otherData.reload) {
        window.location.href = "/buyer/credit"
    } else {
        history.push(`/buyer/credit`);
    }
}

function handleCreditNoteActionType(actionData, history, otherData) {
    if (otherData.reload) {
        window.location.href = `/buyer/order/${
      (actionData || {}).OrderNumber
    }/number?open=credit-notes&credit-note-no=${
      (actionData || {}).CreditNoteNumber
    }`
    } else {
        history.push(
            `/buyer/order/${
      (actionData || {}).OrderNumber
    }/number?open=credit-notes&credit-note-no=${
      (actionData || {}).CreditNoteNumber
    }`
        );
    }
}

function handleCreditLimitActionType(history, otherData, trackingParams, trackParamsCall) {
    if (trackingParams && typeof trackParamsCall == "function") {
        trackParamsCall(trackingParams)
    }

    if (otherData.reload) {
        window.location.href = "/buyer/credit-limit"
    } else {
        history.push(`/buyer/credit-limit`);
    }

    if (otherData.trackPopUp) {
        otherData.trackPopUp();
    }
}

function handleOrderHistoryActionType(history, otherData) {
    if (otherData.reload) {
        window.location.href = "/buyer/order"
    } else {
        history.push(`/buyer/order`);
    }
}

function handleOrderActionType(actionData, history, otherData) {
    if (otherData.reload) {
        window.location.href = `/buyer/order/${(actionData || {}).OrderNumber}/number`;
    } else {
        history.push(`/buyer/order/${(actionData || {}).OrderNumber}/number`);
    }
}

function handleProductActionType(actionData, history, otherData = {}) {
    if (otherData.id !== undefined && otherData.commercialName !== undefined) {
        if (actionData.length !== 0) {
            let redirectUrl = `/buyer/banner?id=${otherData.id}&commercialName=${otherData.commercialName}`
            if (otherData.sendReference) {
                redirectUrl = redirectUrl + `?referenceId=${actionData.ReferenceId}&referenceType=${actionData.ReferenceType}`
            }
            if (otherData.reload) {
                window.location.href = redirectUrl;
            } else {
                history.push(redirectUrl);
            }
        }
    }
}

function handlePayLaterActionType(history, otherData) {
    if (otherData.reload) {
        window.location.href = `/paylater`;
    } else {
        history.push(`/paylater`);
    }
    if (otherData.trackPopUp) {
        otherData.trackPopUp();
    }
}

function handleRewardsActionType(history) {
    history.push('/rewards');
}

function handleProfileActionType(history) {
    history.push('/buyer/profile');
}

function handleHelpActionType(history) {
    history.push('/contact');
}

function getESV2FlagFromQueryParam(param) {
    const searchParam = queryString.parse(window.location.search);
    if (searchParam[param] || searchParam[EsV2QueryParams.ES_V2_FLAG]) {
        if (searchParam[param] === "true" || searchParam[EsV2QueryParams.ES_V2_FLAG] === "true") {
            window.localStorage.setItem(param, "true");
            return true;
        } else {
            window.localStorage.setItem(param, "false");
            return false;
        }
    } else {
        const localStorageVal = window.localStorage.getItem(param)
        return !!(localStorageVal && localStorageVal === "true");
    }
}

function toggleScroll(hideScroll = false) {
  const bodyTag = document.getElementsByTagName("BODY")[0]
  if(hideScroll) {
    bodyTag.style.overflowY = "hidden"
  } else {
    bodyTag.style.overflowY = "scroll"
  }
}

function getOrderDate(date, format) {
    let momentObj = moment(date).format(format).split(" ");
    let test = momentObj.map((item, index) => {
        if (index !== (momentObj.length - 1)) {
            return item.length ? item[0].toUpperCase() + item.slice(1, item.length).toLowerCase() : ""
        } else {
            return item
        }
    })
    return test.join(" ");
}

function handleDismissNudgeCTA(trackingParams, trackParamsCall) {
    if (trackingParams && typeof trackParamsCall == "function") {
        trackParamsCall(trackingParams)
    }
}

export {
    loadingHelper,
    debounce,
    getFormattedINRValues,
    getCache,
    getScroll,
    usePrevious,
    useOnUpdate,
    getThousandsFormatCurrency,
    makeAndSaveBlob,
    saveLink,
    removeExtraPrecision,
    parseIntOr0,
    handleActionType,
    getESV2FlagFromQueryParam,
    toggleScroll,
    getOrderDate
};